import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal } from '../i18n.js';
import Stapelstaven from './Stapelstaven.jsx';
import Lijngrafiek from './Lijngrafiek.jsx';
import Balken from './Balken.jsx';
import Periodes, { duurGrof } from './Periodes.jsx';

/**
 * Alerts in de tijd, op het Overzicht (docs/besluiten/meer-grafieken.md): nieuwe alerts naar ernst, hoeveel er aan het
 * eind van elk tijdvak openstonden, per module, en hoe snel: de mediaan tot oppakken en tot afsluiten door een medewerker.
 *
 * De staven (Bernhard, 2026-10-26): 24 uur, 7 of 30 dagen, bij elke periode zo'n 30 staven (per uur, zes uur of dag), en de ernst in
 * ADTS-blauw van licht (laag) naar donker (kritiek); in donker andersom, zodat kritiek het meest opvalt. Het woord staat in
 * legenda en tooltip.
 */
const ernsten = () => [
  { sleutel: 'neutral', label: t('Laag'), kleur: 'var(--sev-laag)' },
  { sleutel: 'warning', label: t('Midden'), kleur: 'var(--sev-midden)' },
  { sleutel: 'serious', label: t('Hoog'), kleur: 'var(--sev-hoog)' },
  { sleutel: 'critical', label: t('Kritiek'), kleur: 'var(--sev-kritiek)' },
];
const kopVan = (per) => ({ hour: t('Nieuwe alerts per uur'), '6 hours': t('Nieuwe alerts per zes uur') }[per] ?? t('Nieuwe alerts per dag'));

export default function AlertsInDeTijd({ klant }) {
  const [dagen, zetDagen] = useState(7);
  const q = useQuery({ queryKey: ['grafiek', 'alerts', klant, dagen], queryFn: () => api.grafiek('alerts', klant, dagen), refetchInterval: 60_000 });
  const reeksen = ernsten();
  const d = q.data?.data;
  /* Een tegel met een aantal linkt naar Alerts met de filters erop; een mediaan niet (daar hoort geen lijst bij). */
  const tegel = (n, label, extra = '', naar = null) => (naar
    ? <Link className={`tile link ${extra}`} to={naar}><div className="n">{n}</div><div className="t">{label}</div></Link>
    : <div className={`tile ${extra}`}><div className="n">{n}</div><div className="t">{label}</div></div>);
  return (
    <section style={{ marginBottom: 18 }}>
      <div className="group-h"><h2 style={{ margin: 0, fontSize: 16 }}>{t('Alerts in de tijd')}</h2>
        <Periodes keuzes={[1, 7, 30]} waarde={dagen} onKies={zetDagen} /></div>
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : (
        <>
          <div className="tiles">
            {tegel(getal(d.totals.nieuw), t('nieuwe alerts'), '', `/alerts?status=alle&dagen=${dagen}`)}
            {tegel(duurGrof(d.totals.oppakkenS), t('tot oppakken (mediaan)'))}
            {tegel(duurGrof(d.totals.sluitenS), t('tot afsluiten door een medewerker (mediaan)'), '', `/alerts?status=afgesloten&door=medewerker&dagen=${dagen}`)}
            {tegel(getal(d.totals.doorZero), t('vanzelf afgesloten door ADTS ZERO'), '', `/alerts?status=afgesloten&door=zero&dagen=${dagen}`)}
          </div>
          <section className="card" style={{ marginBottom: 18 }}>
            <header><h2>{kopVan(d.per)}</h2>
              <span className="meta legenda">{reeksen.slice().reverse().map((r) => <span key={r.sleutel}><i style={{ background: r.kleur }} />{r.label}</span>)}</span></header>
            <Stapelstaven punten={d.staven} reeksen={reeksen} perUur={d.per !== 'day'} label={kopVan(d.per)} />
          </section>
          <div className="grid-2">
            <section className="card"><header><h2>{t('Open alerts door de tijd')}</h2></header>
              <Lijngrafiek punten={d.staven.map((p) => ({ van: p.van, n: p.open }))} perDag={d.per === 'day'} hoogte={170} eenheid={t('open alerts')} label={t('Open alerts door de tijd')} /></section>
            <section className="card"><header><h2>{t('Per module')}</h2></header>
              <Balken rijen={d.perModule.map((m) => ({ naam: t(m.label), n: m.n }))} /></section>
          </div>
        </>
      )}
    </section>
  );
}
